/**
 * Keep the keyboard open across a re-render.
 *
 * The widget paints by replacing innerHTML, so a render that lands while a
 * field has focus destroys that field: focus is gone, the phone's keyboard
 * slides away, and on Android the field's text flashes selected on its way
 * out. Most renders are a tap away from an input; some are not — the phone
 * lookup that answers half a second after the client has already moved on to
 * the email field was the one that bit (Tim, 2026-10-08).
 *
 * capture() before the paint, restore() after: the same field (by its data
 * attribute) gets focus and its caret back.
 */

export interface FocusMemo {
  key: string;
  start: number | null;
  end: number | null;
  direction: 'forward' | 'backward' | 'none' | null;
}

type Focusable = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function captureFocus(root: Element, attr = 'data-field'): FocusMemo | null {
  const active = (root.ownerDocument || document).activeElement as Focusable | null;
  if (!active || !root.contains(active)) return null;
  const key = active.getAttribute(attr);
  if (!key) return null;
  let start: number | null = null, end: number | null = null;
  let direction: FocusMemo['direction'] = null;
  try {
    // Reading selectionStart throws on input types that have no selection
    // (email, number); those simply get their focus back without a caret.
    const text = active as HTMLInputElement;
    if (typeof text.selectionStart === 'number') {
      start = text.selectionStart;
      end = text.selectionEnd ?? start;
      direction = (text.selectionDirection as FocusMemo['direction']) || null;
    }
  } catch { /* no selection on this control */ }
  return { key, start, end, direction };
}

export function restoreFocus(root: Element, memo: FocusMemo | null, attr = 'data-field'): boolean {
  if (!memo) return false;
  const el = root.querySelector<Focusable>(`[${attr}="${memo.key.replace(/"/g, '\\"')}"]`);
  if (!el || (el as HTMLInputElement).disabled) return false;
  el.focus({ preventScroll: true });
  if (memo.start !== null && 'setSelectionRange' in el) {
    try {
      const len = (el as HTMLInputElement).value.length;
      const s = Math.min(memo.start, len), e = Math.min(memo.end ?? memo.start, len);
      (el as HTMLInputElement).setSelectionRange(s, e, memo.direction || undefined);
    } catch { /* email/number inputs refuse a caret — focus is what matters */ }
  }
  return true;
}
